import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useAxios from "../../../../MainComponents/Hooks/useAxios";
import { toast } from "react-hot-toast";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { Editor as ClassicEditor } from "ckeditor5-custom-build/build/ckeditor";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSpinner } from "@fortawesome/free-solid-svg-icons";

function AddEditDashboardContent({ edit, setReFetch, reFetch }) {
  const [content, setContent] = useState("");
  const [stage, setStage] = useState("");
  const [section, setSection] = useState("");
  const [systemType, setSystemType] = useState("all");
  const [errors, setErrors] = useState("");
  const navigate = useNavigate();
  const { postID } = useParams();

  const [submitContent, setSubmitContent] = useState({
    flag: "",
    dependency: false,
    data: {},
  });

  useEffect(() => {
    if (content !== "") {
      setErrors("");
    } else if (stage !== "") {
      setErrors("");
    }
  }, [content, stage]);

  let api = edit
    ? process.env.REACT_APP_EDIT_DASHBOARD_POST_API
    : process.env.REACT_APP_ADD_DASHBOARD_POST_API;
  const [addEditContentSuccess, addEditContentErrors, submitLoading] = useAxios(
    api,
    "POST",
    submitContent.flag,
    submitContent.dependency,
    submitContent.data,
    true,
  );

  const [ContentDataSuccess] = useAxios(
    `${process.env.REACT_APP_DASHBOARD_POST_INFO_API}${postID}`,
    "GET",
    edit,
  );

  useEffect(() => {
    if (ContentDataSuccess) {
      setStage(ContentDataSuccess.data?.stage);
      setContent(ContentDataSuccess.data?.content);
      setSection(ContentDataSuccess.data?.section);
      setSystemType(ContentDataSuccess.data?.system_type || "all");
    }
  }, [ContentDataSuccess]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (content === "") {
      setErrors("content");
      toast.error("Please Add Content");
    } else if (stage === "") {
      setErrors("stage");
      toast.error("Please Add Stage");
    } else if (+stage === 3 && !section) {
      setErrors("section");
      toast.error("Please Select Section");
    } else if (+stage === 3 && !systemType) {
      setErrors("system_type");
      toast.error("Please Select System");
    } else {
      const data = edit
        ? {
            content: content,
            stage: stage,
            section: section,
            system_type: +stage === 3 ? systemType : "all",
            id: postID,
          }
        : {
            content: content,
            section: section,
            stage: stage,
            system_type: +stage === 3 ? systemType : "all",
          };

      setSubmitContent({
        flag: "addEditContentRequest",
        dependency: !submitContent.dependency,
        data: data,
      });
    }
  };

  useEffect(() => {
    if (addEditContentSuccess) {
      setContent("");
      setStage("");
      setSection("");
      setSystemType("all");
      setErrors("");
      edit ? navigate(-1) : setReFetch(!reFetch);
    }
  }, [addEditContentSuccess]);

  useEffect(() => {
    if (+stage === 3) {
      setSection((prev) => prev || "both");
      setSystemType((prev) => prev || "all");
    } else {
      setSection("");
      setSystemType("all");
    }
  }, [stage]);

  return (
    <section className="flex h-auto w-full flex-col items-center pt-6 dark:bg-dark dark:text-light">
      <form
        method="post"
        onSubmit={handleSubmit}
        className="dashboard-content-form"
      >
        <h2 className="dashboard-content-form__title">
          {edit
            ? "Please fill in the fields to edit the content"
            : "Please fill in the fields to add the content"}
        </h2>

        <div className="dashboard-content-form__block">
          <h3 className="dashboard-content-form__label">Content</h3>
          <div className="dashboard-content-form__editor">
            <CKEditor
              config={{
                toolbar: {
                  items: [
                    "undo",
                    "redo",
                    "|",
                    "imageInsert",
                    "|",
                    "heading",
                    "bold",
                    "italic",
                    "strikethrough",
                    "underline",
                    "link",
                    "blockQuote",
                    "removeFormat",
                    "|",
                    "selectAll",
                    "specialCharacters",
                    "superscript",
                    "subscript",
                    "style",
                    "|",
                    "bulletedList",
                    "numberedList",
                    "outdent",
                    "indent",
                    "|",
                    "mediaEmbed",
                    "insertTable",
                    "|",
                    "fontFamily",
                    "fontColor",
                    "fontBackgroundColor",
                    "fontSize",
                    "highlight",
                    "|",
                    "horizontalLine",
                    "pageBreak",
                    "findAndReplace",
                    "restrictedEditingException",
                    "textPartLanguage",
                    "-",
                  ],
                  shouldNotGroupWhenFull: true,
                },
                language: "en",
                image: {
                  toolbar: [
                    "imageTextAlternative",
                    "toggleImageCaption",
                    "imageStyle:inline",
                    "imageStyle:block",
                    "imageStyle:side",
                  ],
                },
                table: {
                  contentToolbar: [
                    "tableColumn",
                    "tableRow",
                    "mergeTableCells",
                    "tableCellProperties",
                    "tableProperties",
                  ],
                },
              }}
              editor={ClassicEditor}
              data={content}
              onChange={(event, editor) => {
                const data = editor.getData();
                setContent(data);
              }}
            />
          </div>
          {errors === "content" && (
            <p className="dashboard-content-form__error">
              This field is required.
            </p>
          )}
          {addEditContentErrors?.response?.data?.errors?.content && (
            <p className="dashboard-content-form__error">
              {addEditContentErrors.response.data.errors.content[0]}
            </p>
          )}
        </div>

        <div className="dashboard-content-form__fields">
          <div className="dashboard-content-form__block">
            <label className="dashboard-content-form__label" htmlFor="stage">
              Stage
            </label>
            <select
              className="dashboard-content-form__select"
              value={stage?.toString() || ""}
              onChange={(e) => setStage(e.target.value)}
              name="stage"
              id="stage"
            >
              <option value="" disabled hidden>
                Select stage
              </option>
              <option value="1">1st sec</option>
              <option value="2">2nd sec</option>
              <option value="3">3rd sec</option>
            </select>
            {errors === "stage" && (
              <p className="dashboard-content-form__error">
                This field is required.
              </p>
            )}
            {addEditContentErrors?.response?.data?.errors?.stage && (
              <p className="dashboard-content-form__error">
                {addEditContentErrors.response.data.errors.stage[0]}
              </p>
            )}
          </div>

          {+stage === 3 && (
            <div className="dashboard-content-form__block">
              <label
                htmlFor="section"
                className="dashboard-content-form__label"
              >
                Section
              </label>
              <select
                className="dashboard-content-form__select"
                value={section}
                onChange={(e) => setSection(e.target.value)}
                name="section"
                id="section"
              >
                <option value="" disabled hidden>
                  Select Section
                </option>
                <option value="both">Both</option>
                <option value="literary">Literary</option>
                <option value="scientific">Scientific</option>
              </select>
              {errors === "section" && (
                <p className="dashboard-content-form__error">
                  This field is required.
                </p>
              )}
              {addEditContentErrors?.response?.data?.errors?.section && (
                <p className="dashboard-content-form__error">
                  {addEditContentErrors.response.data.errors.section[0]}
                </p>
              )}
            </div>
          )}

          {+stage === 3 && (
            <div className="dashboard-content-form__block">
              <label
                htmlFor="system_type"
                className="dashboard-content-form__label"
              >
                System
              </label>
              <select
                className="dashboard-content-form__select"
                value={systemType}
                onChange={(e) => setSystemType(e.target.value)}
                name="system_type"
                id="system_type"
              >
                <option value="" disabled hidden>
                  Select System
                </option>
                <option value="all">الكل</option>
                <option value="hand_by_hand">ايد بايد</option>
                <option value="normal">النظام العادي</option>
              </select>
              {errors === "system_type" && (
                <p className="dashboard-content-form__error">
                  This field is required.
                </p>
              )}
              {addEditContentErrors?.response?.data?.errors?.system_type && (
                <p className="dashboard-content-form__error">
                  {addEditContentErrors.response.data.errors.system_type[0]}
                </p>
              )}
            </div>
          )}
        </div>

        <button
          disabled={submitLoading && submitContent.flag}
          className="dashboard-content-form__submit"
          type="submit"
        >
          {submitLoading && submitContent.flag ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <>
              <FontAwesomeIcon icon={faPlus} />
              <span>{edit ? "Edit Content" : "Add Content"}</span>
            </>
          )}
        </button>
      </form>
    </section>
  );
}

export default AddEditDashboardContent;
